import React from 'react';

/**
 * Icon — registre SVG monochrome config-driven (DESIGN-SPEC 3.1).
 *
 * Remplace tous les emoji par des glyphes 24x24 dessinés en `currentColor`
 * (la couleur est héritée d'une classe Tailwind recolorée, ex. `text-indigo-700`).
 * Aucune dépendance externe (pas de librairie d'icônes).
 *
 * API :
 *   <Icon name="lock" className="w-5 h-5" />
 *   props: { name: string, className?: string, title?: string }
 *
 * - `aria-hidden="true"` quand décoratif (pas de `title`).
 * - `role="img"` + <title> quand `title` est fourni (accessible).
 * - `name` inconnu -> fallback neutre (petit point) toujours rendu, jamais d'erreur.
 */

// Chaque entrée est le contenu interne du <svg> (viewBox 0 0 24 24).
// stroke="currentColor" 1.5px, linecap/linejoin round — réglés sur le <svg>.
const ICONS = {
  // Cadenas fermé — sécurité / confidentialité.
  lock: (
    <>
      <rect x="5" y="11" width="14" height="9" rx="2" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
      <circle cx="12" cy="15.5" r="1.25" />
    </>
  ),

  // Cadenas ouvert — « sans engagement » / sin tarjeta.
  'no-lock': (
    <>
      <rect x="5" y="11" width="14" height="9" rx="2" />
      <path d="M8 11V8a4 4 0 0 1 7.5-1.9" />
      <circle cx="12" cy="15.5" r="1.25" />
    </>
  ),

  // Document validé — corrigé / officiel.
  'document-check': (
    <>
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
      <path d="M14 3v5h5" />
      <path d="M9 14.5l2 2 4-4" />
    </>
  ),

  // Bouclier — garantie / protection.
  shield: (
    <>
      <path d="M12 3l7 3v5c0 4.5-3 7.8-7 9-4-1.2-7-4.5-7-9V6z" />
    </>
  ),

  // Sceau / verificado — médaille à ruban.
  seal: (
    <>
      <circle cx="12" cy="9" r="5.5" />
      <path d="M9 14l-1.5 6 4.5-2.5L16.5 20 15 14" />
      <path d="M10 9l1.5 1.5L14.5 7.5" />
    </>
  ),

  // Horloge — temps / durée d'examen.
  clock: (
    <>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 8v4l3 2" />
    </>
  ),

  // Graphique — progression / statistiques.
  chart: (
    <>
      <path d="M4 20h16" />
      <rect x="6" y="11" width="3" height="6" rx="0.5" />
      <rect x="11" y="7" width="3" height="10" rx="0.5" />
      <rect x="16" y="13" width="3" height="4" rx="0.5" />
    </>
  ),

  // Livre — cours / contenu / temario.
  book: (
    <>
      <path d="M5 5a2 2 0 0 1 2-2h11v16H7a2 2 0 0 0-2 2z" />
      <path d="M5 5v16" />
      <path d="M18 3v16" />
    </>
  ),

  // Cible — objectif / aprobar.
  target: (
    <>
      <circle cx="12" cy="12" r="8" />
      <circle cx="12" cy="12" r="4.5" />
      <circle cx="12" cy="12" r="1.25" />
    </>
  ),

  // Trophée — réussite.
  trophy: (
    <>
      <path d="M8 4h8v5a4 4 0 0 1-8 0z" />
      <path d="M8 5H5.5a2 2 0 0 0 0 4H8" />
      <path d="M16 5h2.5a2 2 0 0 1 0 4H16" />
      <path d="M12 13v4" />
      <path d="M9 20h6" />
      <path d="M10 17h4l-.5 3h-3z" />
    </>
  ),

  // Étincelle — nouveauté / premium.
  spark: (
    <>
      <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z" />
      <path d="M18.5 15.5l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7z" />
    </>
  ),

  // Coche — validé / fait.
  check: (
    <>
      <path d="M5 12.5l4.5 4.5L19 7" />
    </>
  ),

  // Logo Stripe stylisé (bande monochrome) — paiement sécurisé.
  stripe: (
    <>
      <rect x="3.5" y="6" width="17" height="12" rx="2.5" />
      <path d="M9 14.2c0 1 1 1.6 2.3 1.6 1.4 0 2.4-.6 2.4-1.8 0-2.3-4.4-1.4-4.4-3 0-.6.6-1 1.6-1 1 0 1.8.4 2.2 1" />
    </>
  ),

  // Drapeau — pays / examen national.
  flag: (
    <>
      <path d="M6 21V4" />
      <path d="M6 4h10l-1.5 3.5L16 11H6" />
    </>
  ),

  // Ampoule — astuce / explication.
  bulb: (
    <>
      <path d="M9 16a5 5 0 1 1 6 0c-.6.5-1 1.1-1 2H10c0-.9-.4-1.5-1-2z" />
      <path d="M10 20h4" />
      <path d="M10.5 22h3" />
    </>
  ),

  // Chevron droit — affordance de navigation (carte cliquable).
  'chevron-right': <path d="M9 6l6 6-6 6" />,

  // Flèche gauche — bouton retour.
  'arrow-left': (
    <>
      <path d="M19 12H5" />
      <path d="M11 6l-6 6 6 6" />
    </>
  ),

  // Presse-papiers — liste / quiz / contrôle.
  clipboard: (
    <>
      <rect x="6" y="5" width="12" height="16" rx="2" />
      <rect x="9" y="3" width="6" height="4" rx="1" />
      <path d="M9 11h6" />
      <path d="M9 14.5h6" />
      <path d="M9 18h4" />
    </>
  ),
};

function Icon({ name, className = 'w-5 h-5', title }) {
  const glyph = ICONS[name];

  // Fallback sûr : name inconnu -> petit point neutre, jamais d'erreur.
  const content = glyph ?? <circle cx="12" cy="12" r="2.5" />;

  // Accessibilité : décoratif (aria-hidden) sauf si un title est fourni.
  const a11y = title
    ? { role: 'img', 'aria-label': title }
    : { 'aria-hidden': 'true', focusable: 'false' };

  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...a11y}
    >
      {title ? <title>{title}</title> : null}
      {content}
    </svg>
  );
}

export default Icon;
